import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DbUser } from "../../../../interfaces";
import axios from "../../../../services/axios";
import { useSearch } from "../../../../hooks/useSearch";
import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory, useLocation } from "react-router-dom";
import { generateGtmEvent } from "../../../../Helpers";

interface TeacherSearchProps {
  selected: DbUser | null;
  onSelect: (teacher: DbUser | null) => void;
  schoolId: number;
}

const useStyles = makeStyles((theme) => ({
  root: (props: TeacherSearchProps) => ({
    "& fieldset": {
      border: "2px solid #DEDEDE",
      borderRadius: "0.5rem",
    },
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& input": {
      // color: props.selected ? theme.palette.primary.dark : "#484848",
      // fontWeight: props.selected ? "500" : "unset",
      color: theme.palette.text.secondary,
      "&::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },
    "& svg": {
      // color: props.selected ? theme.palette.primary.main : "unset",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: "1rem",
      paddingRight: "1rem !important",
      "&:hover fieldset": {
        border: `2px solid ${theme.palette.primary.main}`,
      },
    },
  }),
  schoolIcon: {
    transform: "translateY(-2px)",
  },
  closeButton: {
    color: theme.palette.text.secondary,
    "&:hover": {
      color: "#E74973",
    },
  },
  dropdownIcon: {
    color: theme.palette.text.secondary,
    marginLeft: "0.5rem",
  },
}));

export const TeacherSearch = (props: TeacherSearchProps) => {
  const classes = useStyles(props);
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  let queryParams = new URLSearchParams(location.search);
  const teacherId = queryParams.get("teacherId");
  const [inputFocused, setInputFocused] = useState(false);
  const [inputHovered, setInputHovered] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { result, loading, onSearchChange, searchText } = useSearch({
    queryFn: async (state) => {
      const res = await axios.get<DbUser[]>("/teachers", {
        signal: state.signal,
        params: {
          schoolId: props.schoolId,
          search: state.searchText,
        },
      });
      return res.data;
    },
    defaultValue: [] as DbUser[],
  });
  const { onSelect } = props;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // useEffect(() => {
  //   onSearchChange("");
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // On initial load, set the selection and update the field with teacher
  useEffect(() => {
    if (teacherId && result.length > 0 && !initialized) {
      let teacher = result.find((teacher) => teacher.id === teacherId);
      onSelectRef.current(teacher ?? null);
      if (teacher) {
        setInitialized(true);
      }
    }
  }, [result, teacherId, initialized]);

  return (
    <Autocomplete
      onMouseEnter={() => setInputHovered(true)}
      onMouseLeave={() => setInputHovered(false)}
      disablePortal
      blurOnSelect
      id="teacher-search"
      value={props.selected ?? null}
      onChange={(e, teacher) => {
        history.push({
          search: new URLSearchParams({
            teacherId: teacher?.id ?? "",
            schoolId: props.schoolId.toString(),
          }).toString(),
        });
        generateGtmEvent({
          event: "select",
          action: "click",
          name: "onclick",
          type: "select a teacher",
          region: "main content",
          section: "all classes",
          text: `${teacher?.full_name}`,
        });
        props.onSelect(teacher);
      }}
      open={open}
      onInputChange={(e, value, reason) => {
        onSearchChange(value);
        // Prevent opening on selection
        // and close when text is empty
        if (reason !== "reset") {
          setOpen(!!value.length);
        }
      }}
      onOpen={() => {
        // We want to keep the menu closed
        // until the user types in order to
        // prevent showing the "no options"
        // message when there is empty text.
        if (searchText.length) {
          setOpen(true);
        }
      }}
      onClose={() => setOpen(false)}
      options={result}
      getOptionLabel={(teachers) => teachers.full_name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      style={{ width: 300 }}
      className={classes.root}
      filterOptions={(v) => v}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.full_name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputFocused || props.selected ? "Teacher" : ""}
          placeholder="Select a teacher"
          onFocus={(e) => {
            setInputFocused(true);
          }}
          onChange={(e) => {
            generateGtmEvent({
              event: "form",
              action: "click",
              name: "onclick",
              type: "blur",
              region: "main content",
              section: "all classes ^ teacher",
              text: `${e.target.value}`,
            });
          }}
          onBlur={(e) => {
            setInputFocused(false);
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" className={classes.schoolIcon}>
                <i className="fa-regular fa-user fa-1x"></i>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end" className={classes.schoolIcon}>
                {inputHovered && (props.selected || (teacherId && result.length > 0)) ? (
                  <Tooltip
                    placement="bottom"
                    title="Clear current search"
                    arrow
                    role="tooltip"
                    describeChild>
                    <IconButton
                      sx={{ width: "1.5rem", height: "1.5rem" }}
                      onClick={() => {
                        props.onSelect(null);
                        setOpen(false);
                      }}
                      aria-label="clear current search"
                      className={classes.closeButton}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {open ? (
                  <Tooltip
                    placement="bottom"
                    title="Close list"
                    arrow
                    role="tooltip"
                    describeChild>
                    <IconButton
                      sx={{ width: "2rem", height: "2rem" }}
                      onClick={() => {
                        setOpen(false);
                      }}
                      aria-label="close list"
                      className={classes.dropdownIcon}>
                      <i className="fa-regular fa-chevron-up fa-1x"></i>
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    placement="bottom"
                    title="Open list"
                    arrow
                    role="tooltip"
                    describeChild>
                    <IconButton
                      sx={{ width: "2rem", height: "2rem" }}
                      onClick={() => {
                        onSearchChange("");
                        setOpen(true);
                      }}
                      aria-label="Open list"
                      className={classes.dropdownIcon}>
                      <i className="fa-regular fa-chevron-down fa-1x"></i>
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          }}
        />
      )}
      popupIcon={null}
      clearIcon={null}
    />
  );
};
